import { useEffect, useMemo, useState } from 'react';
import { useDragScroll } from './useDragScroll';
import type { MouseEvent, ReactNode } from 'react';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Inbox, SlidersHorizontal,
} from 'lucide-react';
import { ExpandedRow } from './ExpandedRow';
import { cn } from './cn';
import { Dropdown } from './Dropdown';
import { Checkbox } from './Checkbox';

export interface DataTableColumn<T> {
  key?: string;
  title: ReactNode;
  dataIndex?: string | string[];
  render?: (value: any, record: T, index: number) => ReactNode;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  sorter?: (a: T, b: T) => number;
  defaultVisible?: boolean;
  alwaysVisible?: boolean;
  ellipsis?: boolean;
}

export interface DataTablePagination {
  pageSize?: number;
  current?: number;
  total?: number;
  showSizeChanger?: boolean;
  onChange?: (page: number, pageSize: number) => void;
}

export interface DataTableProps<T> {
  /** Persists column-visibility + density to localStorage. */
  storageKey: string;
  columns: DataTableColumn<T>[];
  dataSource: T[];
  rowKey?: string | ((r: T) => string | number);
  loading?: boolean;
  pagination?: false | DataTablePagination;
  toolbarLeft?: ReactNode;
  toolbarRight?: ReactNode;
  onRow?: (record: T, index: number) => { onClick?: (e: MouseEvent) => void } | undefined;
  expandable?: {
    expandedRowRender: (record: T, index: number) => ReactNode;
    rowExpandable?: (record: T) => boolean;
  };
  emptyText?: ReactNode;
  /** Min content width before horizontal scroll kicks in. */
  scrollX?: number;
  /**
   * Let the table BODY take the height its container has left, and scroll inside it.
   *
   * Off by default, so every existing page keeps the layout it has: the table grows to fit its rows
   * and the document scrolls. Opt in from a page that owns a fixed-height workspace (the Reports
   * console) — the toolbar and the pager stay put, the rows scroll under the sticky header, and the
   * page stops being one long column that has to be scrolled past to reach the pagination.
   */
  fillHeight?: boolean;
  className?: string;
}

type Density = 'compact' | 'default' | 'spacious';
const PAD: Record<Density, string> = {
  compact: 'px-3 py-1.5',
  default: 'px-3.5 py-2.5',
  spacious: 'px-4 py-3.5',
};
const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

const getByPath = (obj: any, path?: string | string[]) => {
  if (path == null) return undefined;
  if (Array.isArray(path)) return path.reduce((o, k) => (o == null ? o : o[k]), obj);
  return obj?.[path];
};

function keyOf<T>(c: DataTableColumn<T>): string {
  return c.key ?? (Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : String(c.dataIndex ?? c.title));
}

/**
 * Tailwind data grid (replaces the antd-based DataTable) on TanStack Table.
 * Keeps the same column API, so list pages migrate by swapping the import.
 * Sticky header, client/server pagination, sort, density, column-picker
 * (persisted), expandable rows, loading + empty states.
 */
export function DataTable<T extends object>({
  storageKey, columns, dataSource, rowKey = 'id', loading, pagination = {},
  toolbarLeft, toolbarRight, onRow, expandable, emptyText = 'No data', scrollX, fillHeight, className,
}: DataTableProps<T>) {
  const allKeys = useMemo(() => columns.map(keyOf), [columns]);
  const defaultVisible = useMemo(() => columns.filter((c) => c.defaultVisible !== false).map(keyOf), [columns]);

  // ── persisted column visibility + density ──
  const visKey = `gb:tbl:${storageKey}:visible`;
  const knownKey = `gb:tbl:${storageKey}:known`;
  const densKey = `gb:tbl:${storageKey}:density`;
  /**
   * Stored visibility is an ALLOW-LIST, which means a column added to a table AFTER someone first
   * opened it was invisible to them forever: their saved list simply didn't name it, and there was
   * no way to tell "the user hid this" apart from "this didn't exist yet". Every returning user
   * silently missed new columns, and the only workaround was renaming the storageKey and throwing
   * away their choices.
   *
   * So we also persist the set of keys the table KNEW about at save time. Anything not in that set
   * is new — it appears if it wants to (`defaultVisible !== false`) — while a key the user actually
   * unticked stays hidden, because it IS in the known set. Old saves (a bare array, no known set)
   * are read as "knew about everything currently visible", so nobody's layout jumps on upgrade.
   */
  const [visible, setVisible] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(visKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!Array.isArray(parsed)) return defaultVisible;
      const saved = parsed.filter((k: string) => allKeys.includes(k));
      if (!saved.length) return defaultVisible;
      const knownRaw = localStorage.getItem(knownKey);
      const known = new Set<string>(
        Array.isArray(JSON.parse(knownRaw ?? 'null')) ? JSON.parse(knownRaw!) : saved,
      );
      const fresh = defaultVisible.filter((k) => !known.has(k) && !saved.includes(k));
      // Keep the table's own column order rather than appending new keys at the end.
      return fresh.length ? allKeys.filter((k) => saved.includes(k) || fresh.includes(k)) : saved;
    } catch { return defaultVisible; }
  });
  // Row density is fixed — the compact/default/spacious picker was removed from every table, so a
  // previously persisted choice is ignored (and its key cleared) rather than stranding someone on a
  // setting they can no longer change.
  const density: Density = 'default';
  useEffect(() => {
    try {
      localStorage.setItem(visKey, JSON.stringify(visible));
      // Record what the table offered at save time, so the next release can tell a NEW column apart
      // from one this user deliberately hid.
      localStorage.setItem(knownKey, JSON.stringify(allKeys));
    } catch { /* ignore */ }
  }, [visKey, knownKey, visible, allKeys]);
  useEffect(() => { try { localStorage.removeItem(densKey); } catch { /* ignore */ } }, [densKey]);

  const visibleSet = useMemo(() => new Set(visible), [visible]);
  const shownColumns = columns.filter((c) => c.alwaysVisible || visibleSet.has(keyOf(c)));

  /**
   * How wide this table WANTS to be — the sum of what its visible columns asked for.
   *
   * `w-full` + `table-layout: auto` treats a column's `width` as a hint and then squeezes everything
   * to fit the container, so switching on a dozen optional columns produced a cram of 40px cells and
   * truncated headers ("2…" where a GST number should be) instead of a table you scroll. Pages could
   * pass `scrollX`, but that is a single hard-coded number that has no idea which columns the user
   * has actually revealed.
   *
   * So the minimum width is derived from the visible set and grows as columns are switched on. With
   * `table-layout: fixed` (below) each column then gets exactly the width it declared, and the
   * container scrolls — which is what the horizontal scrollbar was always there for.
   */
  const naturalWidth = useMemo(() => {
    const DEFAULT_COL = 150; // a column that declares no width still needs room to be readable
    return shownColumns.reduce((sum, c) => sum + (typeof c.width === 'number' ? c.width : DEFAULT_COL), 0)
      + (expandable ? 36 : 0);
  }, [shownColumns, expandable]);
  const minWidth = Math.max(naturalWidth, scrollX ?? 0);
  // Fixed layout honours the declared widths instead of redistributing by content — but only when
  // there is a real minimum to lay out against, so a narrow table still sizes itself naturally.
  const layoutFixed = shownColumns.every((c) => c.width != null);
  const { ref: scrollRef, pannable } = useDragScroll<HTMLDivElement>();

  // ── server vs client pagination ──
  const serverPaginated = pagination !== false && (pagination.current != null || pagination.total != null);

  const [sorting, setSorting] = useState<SortingState>([]);
  const tanColumns = useMemo<ColumnDef<T>[]>(
    () => shownColumns.map((c) => ({
      id: keyOf(c),
      accessorFn: (row: T) => getByPath(row, c.dataIndex),
      enableSorting: !!(c.sortable || c.sorter),
      sortingFn: c.sorter ? (a, b) => c.sorter!(a.original, b.original) : 'auto',
    })),
    [shownColumns],
  );

  const table = useReactTable({
    data: dataSource,
    columns: tanColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: pagination !== false && !serverPaginated ? getPaginationRowModel() : undefined,
    manualPagination: serverPaginated,
    initialState: pagination !== false && !serverPaginated ? { pagination: { pageSize: pagination.pageSize ?? 10 } } : undefined,
  });

  const rows = table.getRowModel().rows;

  const [expanded, setExpanded] = useState<Set<string | number>>(new Set());
  const keyForRow = (r: T, i: number) => (typeof rowKey === 'function' ? rowKey(r) : (r as any)[rowKey] ?? i);
  const toggleExpand = (k: string | number) =>
    setExpanded((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  // ── pagination footer numbers ──
  const clientPage = table.getState().pagination.pageIndex;
  const clientPageSize = table.getState().pagination.pageSize;
  const total = serverPaginated ? (pagination as DataTablePagination).total ?? dataSource.length : dataSource.length;
  const curPage = serverPaginated ? ((pagination as DataTablePagination).current ?? 1) : clientPage + 1;
  const curSize = serverPaginated ? (pagination as DataTablePagination).pageSize ?? 10 : clientPageSize;
  const pageCount = Math.max(1, Math.ceil(total / curSize));
  const from = total === 0 ? 0 : (curPage - 1) * curSize + 1;
  const to = Math.min(curPage * curSize, total);
  const goPage = (p: number) => {
    if (serverPaginated) (pagination as DataTablePagination).onChange?.(p, curSize);
    else table.setPageIndex(p - 1);
  };

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-1 flex-wrap items-center gap-2">{toolbarLeft}</div>
        <div className="flex flex-wrap items-center gap-2">
          {toolbarRight}
          <Dropdown
            trigger="click"
            align="end"
            panelClassName="min-w-[220px] p-2"
            panel={
              <div>
                <div className="px-1 pb-2 type-label-strong text-subtle">Show columns</div>
                <div className="flex flex-col gap-1.5">
                  {columns.filter((c) => !c.alwaysVisible).map((c) => {
                    const k = keyOf(c);
                    return (
                      <Checkbox
                        key={k}
                        checked={visibleSet.has(k)}
                        onCheckedChange={(on) => setVisible((v) => (on ? [...v, k] : v.filter((x) => x !== k)))}
                        label={typeof c.title === 'string' ? c.title : k}
                      />
                    );
                  })}
                </div>
                <div className="mt-2 flex gap-3 border-t border-line pt-2 text-xs">
                  <button type="button" className="text-brand-700 hover:underline" onClick={() => setVisible(allKeys)}>Show all</button>
                  <button type="button" className="text-muted hover:underline" onClick={() => setVisible(defaultVisible)}>Reset</button>
                </div>
              </div>
            }
          >
            <button type="button" aria-label="Column settings" className="grid size-8 place-items-center rounded-control border border-line text-muted transition-colors hover:border-brand-300 hover:text-brand-700">
              <SlidersHorizontal size={16} />
            </button>
          </Dropdown>
        </div>
      </div>

      {/* Table */}
      {/* Grab-and-drag panning: a wide table is otherwise only reachable by trackpad or by hunting
          for a scrollbar that may be a hundred rows below. The cursor only offers the grab when the
          content actually overflows. */}
      <div
        ref={scrollRef}
        className={cn(
          'overflow-x-auto rounded-card border border-line shadow-e1',
          // The sticky header only has something to stick TO once this box has a height of its own.
          fillHeight && 'min-h-0 flex-1 overflow-y-auto',
          pannable && 'cursor-grab data-[panning=true]:cursor-grabbing data-[panning=true]:select-none',
        )}
      >
        <table
          className="w-full border-collapse text-sm"
          style={{ minWidth, tableLayout: layoutFixed ? 'fixed' : undefined }}
        >
          {/* Tinted to match components/TwDataTable, so the app's two table implementations do not
              disagree about what a header looks like. `bg-surface-2` stays underneath the gradient:
              this header is STICKY, so rows scroll beneath it and it must be fully opaque — a
              gradient alone would let the data show through as it passes. */}
          <thead className="sticky top-0 z-10 bg-surface-2 bg-linear-to-b from-brand-500/[0.14] to-brand-500/[0.04] dark:from-brand-400/[0.13] dark:to-brand-400/[0.03]">
            <tr className="border-b border-line">
              {expandable && <th className="w-9" />}
              {shownColumns.map((c) => {
                const col = table.getColumn(keyOf(c));
                const sortDir = col?.getIsSorted();
                return (
                  <th
                    key={keyOf(c)}
                    style={{ width: c.width }}
                    className={cn('whitespace-nowrap px-3.5 py-2.5 type-overline text-muted', ALIGN[c.align ?? 'left'])}
                  >
                    {col?.getCanSort() ? (
                      <button type="button" onClick={col.getToggleSortingHandler()} className="inline-flex items-center gap-1 hover:text-content">
                        {c.title}
                        {sortDir === 'asc' ? <ArrowUp size={14} strokeWidth={2.5} className="text-brand-600 dark:text-brand-400" /> : sortDir === 'desc' ? <ArrowDown size={14} strokeWidth={2.5} className="text-brand-600 dark:text-brand-400" /> : <ArrowUpDown size={14} className="text-line-strong" />}
                      </button>
                    ) : (
                      c.title
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk${i}`} className="border-b border-line">
                  {expandable && <td className="px-3.5 py-3" />}
                  {shownColumns.map((c) => (
                    <td key={keyOf(c)} className={cn(PAD[density])}>
                      <div className="h-3.5 w-3/4 animate-pulse rounded bg-surface-3" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={shownColumns.length + (expandable ? 1 : 0)}>
                  <div className="flex flex-col items-center justify-center gap-2 py-14 text-subtle">
                    <Inbox size={24} className="opacity-50" />
                    <span className="text-sm">{emptyText}</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const rec = row.original;
                const rk = keyForRow(rec, row.index);
                const rowProps = onRow?.(rec, row.index);
                const isExpanded = expanded.has(rk);
                const canExpand = expandable && (expandable.rowExpandable?.(rec) ?? true);
                return (
                  <FragmentRow key={rk}>
                    <tr
                      onClick={rowProps?.onClick}
                      className={cn('border-b border-line transition-colors hover:bg-brand-500/[0.055] dark:hover:bg-brand-400/[0.08]', rowProps?.onClick && 'cursor-pointer')}
                    >
                      {expandable && (
                        <td className="w-9 pl-2">
                          {canExpand && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleExpand(rk); }}
                              className="grid size-6 place-items-center rounded-md text-muted transition-colors hover:bg-surface-3 hover:text-content"
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              <ChevronRight size={16} className={cn('transition-transform', isExpanded && 'rotate-90')} />
                            </button>
                          )}
                        </td>
                      )}
                      {shownColumns.map((c) => {
                        const val = getByPath(rec, c.dataIndex);
                        return (
                          <td
                            key={keyOf(c)}
                            // `max-w-[1px]` is the trick that makes truncation work under table-layout:auto,
                            // where a cell otherwise grows to fit its content. Under fixed layout the column
                            // width is already authoritative, and that 1px cap would collapse the cell.
                            // `wrap-break-word` otherwise: a value with no space in it ("DEF007P/1788164304276")
                            // is wider than a fixed column and used to paint straight over the column beside
                            // it. It now wraps inside its own cell; text that fits is untouched.
                            className={cn('text-content', PAD[density], ALIGN[c.align ?? 'left'], c.ellipsis ? (layoutFixed ? 'truncate' : 'max-w-[1px] truncate') : 'wrap-break-word')}
                            title={c.ellipsis && typeof val === 'string' ? val : undefined}
                          >
                            {c.render ? c.render(val, rec, row.index) : (val as ReactNode)}
                          </td>
                        );
                      })}
                    </tr>
                    {expandable && isExpanded && (
                      <ExpandedRow colSpan={shownColumns.length + 1}>
                        {expandable.expandedRowRender(rec, row.index)}
                      </ExpandedRow>
                    )}
                  </FragmentRow>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / pagination */}
      {pagination !== false && total > 0 && (
        /* `pr-20` only in the fill-height frame: that layout reaches the bottom of the viewport,
            where the chat button sits — the app shell's own bottom padding is what keeps the two
            apart on an ordinary scrolling page. */
        <div className={cn('mt-3 flex flex-wrap items-center justify-end gap-3 text-sm text-muted', fillHeight && 'shrink-0 pr-20')}>
          <div className="flex items-center gap-1">
            <span className="px-2 tabular-nums">{from}–{to} of {total}</span>
            <button type="button" disabled={curPage <= 1} onClick={() => goPage(curPage - 1)} className="grid size-8 place-items-center rounded-control border border-line text-muted transition-colors hover:border-brand-300 hover:text-brand-700 disabled:opacity-40 disabled:hover:border-line disabled:hover:text-muted">
              <ChevronLeft size={16} />
            </button>
            <span className="px-2 tabular-nums">Page {curPage} / {pageCount}</span>
            <button type="button" disabled={curPage >= pageCount} onClick={() => goPage(curPage + 1)} className="grid size-8 place-items-center rounded-control border border-line text-muted transition-colors hover:border-brand-300 hover:text-brand-700 disabled:opacity-40 disabled:hover:border-line disabled:hover:text-muted">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// React fragment with a key (rows + expansion row need to share a key).
function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
